import { dist, map } from '@utils/math/maths.js';

const BLACKLISTED_TAGS = [ 'TEXTAREA', 'SELECT', 'INPUT', 'BUTTON', 'A' ]
    .reduce((p, v) => (p[v] = true, p), {});

function Vec2(x = 0, y = 0) { this.x = x; this.y = y; }
const tPos = new Vec2();

const EMPTY_ARRAY = [];
const MOCKTOUCHES = [ { identifier: -35410, clientX: 0, clientY: 0 } ];
function mockTouches(e) {
    MOCKTOUCHES[0].clientX = e.clientX ?? 0;
    MOCKTOUCHES[0].clientY = e.clientY ?? 0;
    return MOCKTOUCHES;
}

let isPrimaryTouch = false;
let primaryTouchTimer;
const resetPrimaryTouch = () => { isPrimaryTouch = false; };
function setPrimaryTouch() {
    isPrimaryTouch = true;
    clearTimeout(primaryTouchTimer);
    primaryTouchTimer = setTimeout(resetPrimaryTouch, 200);
}

function Finger(name) {
    this.name = name;
    this.position = { x: 0, y: 0 };
    this.identifier = -35000;
    this.used = false;
    this.use = (touch) => {
        this.used = true;
        this.id = touch.identifier;
        this.position.x = touch.clientX ?? 0;
        this.position.y = touch.clientY ?? 0;
    };
    this.unuse = () => { this.used = false; this.id = -35000; };
}


export default class TouchController {
    constructor(opts = {}) {
        // opts.viewport = ViewportManager instance for normalized coordinates
        this._viewport = opts.viewport ?? null;
        this._canSecondaryClick = opts.canSecondaryClick ?? false;
        this._canWheelClick = opts.canWheelClick ?? false;
        this._preventZoom = opts.preventZoom ?? false;

        this._finger1 = new Finger('Finger 1');
        this._finger2 = new Finger('Finger 2');
        this._fingers = [ this._finger1, this._finger2 ];

        this._needsClickIn = false;
        this._needsClickOut = false;
        this._needsClicked = false;
        this._needsDoubleTap = false;
        this._pressedAt = 0;
        this._isListening = false;
        this._tapFingerCount = 0;
        this._tapDate = 0;
        this._doubleTouchTimer = null;
        this._doubleTouch = false;
        this._lastTouchTarget = null;
        this._doubleTouchX = Number.MAX_SAFE_INTEGER;
        this._doubleTouchY = Number.MAX_SAFE_INTEGER;

        this.target = { value: null };

        const firstPos = new Vec2();
        const prevPos = new Vec2();
        const pos = new Vec2();
        const relativePos = new Vec2();
        const delta = new Vec2();
        const normalizePos = new Vec2();
        const normalizeRelativePos = new Vec2();

        this.state = {
            isMultitouch: false,
            useTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 1,
            canHover: false,
            restrictMove: false,
            hasStylus: false,
            isStylus: false,
            pressure: 0,
            pressed: false,
            primaryPressed: false,
            secondaryPressed: false,
            wheelPressed: false,
            clicked: false,
            clickIn: false,
            clickOut: false,
            doubleTapped: false,
            doubleTapped2Fingers: false,
            firstPos, prevPos, pos, relativePos, delta,
            normalizePos, normalizeRelativePos,
            initialPinch: 0, prevPinch: 0, pinch: 0, pinchDelta: 0,
            initialAng: 0, prevAng: 0, ang: 0, angDelta: 0,
            fingerCount: 0, realFingerCount: 0, maxFingerCount: 0,
            fingers: this._fingers,
            target: this.target,
            pressedTime: 0,
            element: null
        };

        this._onStart = this._onStart.bind(this);
        this._onMove = this._onMove.bind(this);
        this._onEnd = this._onEnd.bind(this);
        this._onContextMenu = this._onContextMenu.bind(this);
    }


    get _vpWidth() { return this._viewport ? this._viewport.width : window.innerWidth; }
    get _vpHeight() { return this._viewport ? this._viewport.height : window.innerHeight; }


    listen() {
        if (this._isListening) return;
        this._isListening = true;
        const wi = window;
        const opts = { passive: false };
        wi.addEventListener('touchstart', this._onStart, opts);
        wi.addEventListener('touchmove', this._onMove, opts);
        wi.addEventListener('touchend', this._onEnd, opts);
        wi.addEventListener('touchcancel', this._onEnd, opts);
        wi.addEventListener('mousedown', this._onStart, opts);
        wi.addEventListener('mousemove', this._onMove, opts);
        wi.addEventListener('mouseup', this._onEnd, opts);
        if (this._canSecondaryClick) {
            wi.addEventListener('contextmenu', this._onContextMenu, opts);
        }
    }


    poll() {
        const s = this.state;
        s.delta.x = s.pos.x - s.prevPos.x;
        s.delta.y = s.pos.y - s.prevPos.y;
        s.prevPos.x = s.pos.x;
        s.prevPos.y = s.pos.y;
        s.pinchDelta = s.pinch - s.prevPinch;
        s.angDelta = s.ang - s.prevAng;
        s.prevPinch = s.pinch;
        s.prevAng = s.ang;
        s.clickIn = this._needsClickIn;
        s.clickOut = this._needsClickOut;
        s.clicked = this._needsClicked;

        if (s.pressed || s.clickOut) s.pressedTime = performance.now() - this._pressedAt;
        else s.pressedTime = 0;

        if (this._needsDoubleTap) {
            if (this._tapFingerCount === 2) s.doubleTapped2Fingers = true;
            else s.doubleTapped = true;
            this._tapDate = 0;
        } else {
            s.doubleTapped = false;
            s.doubleTapped2Fingers = false;
        }

        this._needsClicked = false;
        this._needsClickIn = false;
        this._needsClickOut = false;
        this._needsDoubleTap = false;
    }


    _canControl(e) {
        if (!e) return true;
        const el = e.target;
        if (!el) return true;
        if (BLACKLISTED_TAGS[el.tagName]) return false;
        return true;
    }


    _getFinger(touch) {
        if (!touch || touch.identifier === undefined) return;
        if (this._finger1.id === touch.identifier) return this._finger1;
        if (this._finger2.id === touch.identifier) return this._finger2;
        return null;
    }


    _updatePositions(start, forcePosition) {
        const s = this.state;
        const f1 = this._finger1;
        const f2 = this._finger2;
        const pos = s.pos;
        const isMultitouch = s.fingerCount > 1;

        if (isMultitouch && !forcePosition) {
            const x = (f1.position.x + f2.position.x) * 0.5;
            const y = (f1.position.y + f2.position.y) * 0.5;
            const distance = dist(f1.position.x, f1.position.y, f2.position.x, f2.position.y);
            const ang = Math.atan2(f2.position.y - f1.position.y, f2.position.x - f1.position.x);
            if (start) { s.initialPinch = distance; s.initialAng = ang; }
            s.pinch = distance - s.initialPinch;
            s.ang = ang - s.initialAng;
            pos.x = x;
            pos.y = y;
        } else {
            let newPos = pos;
            if (forcePosition) newPos = forcePosition;
            else if (f1.used) newPos = f1.position;
            else if (f2.used) newPos = f2.position;
            s.initialPinch = s.pinch = 0;
            s.initialAng = s.ang = 0;
            pos.x = newPos.x;
            pos.y = newPos.y;
        }

        if (start) {
            s.delta.x = s.delta.y = 0;
            s.prevPos.x = s.firstPos.x = pos.x;
            s.prevPos.y = s.firstPos.y = pos.y;
            s.prevPinch = s.pinch;
            s.prevAng = s.ang;
            s.angDelta = 0;
            s.pinchDelta = 0;
        }
    }


    _updateNormalizePos() {
        const s = this.state;
        if (s.pressed) {
            s.relativePos.x = s.pos.x - s.firstPos.x;
            s.relativePos.y = s.pos.y - s.firstPos.y;
        } else {
            s.relativePos.x = s.relativePos.y = 0;
        }
        const vpw = this._vpWidth;
        const vph = this._vpHeight;
        s.normalizePos.x = map(s.pos.x, 0, vpw, -1, 1);
        s.normalizePos.y = map(s.pos.y, 0, vph, 1, -1);
        s.normalizeRelativePos.x = s.relativePos.x / vpw;
        s.normalizeRelativePos.y = s.relativePos.y / vph;
    }


    _onContextMenu(e) {
        if (!!e.changedTouches) return;
        e.preventDefault();
    }


    _onStart(e) {
        const s = this.state;
        const isTouch = !!e.changedTouches;
        if (isTouch) setPrimaryTouch();
        else if (isPrimaryTouch) return;

        let isPrimaryClick = false;
        let isWheelClick = false;
        let isSecondaryClick = false;
        if (!isTouch) {
            if (s.pressed) return;
            isPrimaryClick = e.button === 0;
            isWheelClick = this._canWheelClick && e.button === 1;
            isSecondaryClick = this._canSecondaryClick && e.button === 2;
            if (!isPrimaryClick && !isSecondaryClick && !isWheelClick) return;
        }

        if (isTouch && this._preventZoom) {
            const x = e.changedTouches[0].clientX;
            const y = e.changedTouches[0].clientY;
            const touchDist = dist(x, y, this._doubleTouchX, this._doubleTouchY);
            this._doubleTouchX = x;
            this._doubleTouchY = y;
            if (touchDist > 60) this._doubleTouch = false;
            if (this._lastTouchTarget !== e.target && touchDist > 10) this._doubleTouch = false;
            this._lastTouchTarget = e.target;
            if (this._doubleTouch) {
                this._doubleTouch = false;
                e.preventDefault();
                e.stopPropagation();
            } else {
                this._doubleTouch = true;
                clearTimeout(this._doubleTouchTimer);
                this._doubleTouchTimer = setTimeout(() => { this._doubleTouch = false; }, 500);
            }
        }

        if (isTouch && s.fingerCount >= 2) return;
        if (!this._canControl(e)) return;

        const activeEl = document.activeElement;
        if (activeEl && BLACKLISTED_TAGS[activeEl.tagName]) activeEl.blur();

        s.useTouch = !!(isTouch || isPrimaryTouch);
        e.preventDefault();

        const touches = isTouch ? e.changedTouches : mockTouches(e);
        s.realFingerCount = (isTouch ? e.touches : mockTouches(e)).length;

        let needsPosUpdate = false;
        let isFirst = false;
        let isStylus = false;

        for (let i = 0; i < touches.length; i++) {
            const first = s.fingerCount === 0;
            const touch = touches[i];
            const stylus = touch.touchType === 'stylus';
            let finger = this._getFinger(touch);
            if (stylus) s.hasStylus = true;
            if (finger || s.fingerCount >= 2) continue;
            if (first) { isFirst = true; isStylus = stylus; }
            needsPosUpdate = true;
            finger = this._finger1.used ? this._finger2 : this._finger1;
            finger.use(touch);
            s.fingerCount++;
        }

        if (s.fingerCount > 1) isStylus = false;
        s.maxFingerCount = Math.max(s.maxFingerCount, s.fingerCount);

        if (!needsPosUpdate) return;

        if (isFirst) {
            this.target.value = e.target;
            s.pressed = true;
            s.pressedTime = 0;
            s.primaryPressed = !isSecondaryClick && !isWheelClick;
            s.wheelPressed = isWheelClick;
            s.secondaryPressed = isSecondaryClick;
            this._needsClickOut = false;
            this._needsClickIn = true;
            if (s.useTouch) s.canHover = false;
            this._pressedAt = performance.now();
            s.element = e.target;
        }

        s.isStylus = isStylus;
        s.isMultitouch = s.fingerCount > 1;
        s.pressure = (s.isStylus && touches[0]) ? (touches[0].force ?? 0) : 0;

        this._updatePositions(true);
        this._updateNormalizePos();
    }


    _onMove(e) {
        const s = this.state;
        const isTouch = !!e.changedTouches;
        if (isPrimaryTouch && !isTouch) return;
        s.useTouch = !!(isTouch || isPrimaryTouch);
        if (s.useTouch) s.canHover = false;
        else if (!s.pressed) s.canHover = true;

        if (!isTouch && s.pressed && e.buttons === 0) {
            e.isForced = true;
            return this._onEnd(e);
        }

        if (isTouch && this._preventZoom && e.touches.length > 1 && !e.target.dataset.allowPinch) {
            e.preventDefault();
        }

        if (s.restrictMove && !this._canControl(e)) return;

        const touches = s.useTouch ? e.changedTouches : mockTouches(e);
        s.realFingerCount = (isTouch ? e.touches : mockTouches(e)).length;

        let needsPosUpdate = false;
        if (!touches) return;

        if (s.pressed) {
            for (let i = 0; i < touches.length; i++) {
                const touch = touches[i];
                const finger = this._getFinger(touch);
                if (!finger) continue;
                needsPosUpdate = true;
                finger.position.x = touch.clientX ?? 0;
                finger.position.y = touch.clientY ?? 0;
            }
            s.pressure = (s.isStylus && touches[0]) ? (touches[0].force ?? 0) : 0;
            if (!needsPosUpdate) return;
            this._updatePositions();
            this._updateNormalizePos();
        } else if (!s.useTouch) {
            tPos.x = e.clientX ?? 0;
            tPos.y = e.clientY ?? 0;
            this._updatePositions(false, tPos);
            this._updateNormalizePos();
        }
    }


    _onEnd(e) {
        const s = this.state;
        const isTouch = !!e.changedTouches;
        if (isTouch) setPrimaryTouch();
        else if (isPrimaryTouch) return;

        let isPrimaryClick = false;
        let isWheelClick = false;
        let isSecondaryClick = false;
        if (!isTouch) {
            isPrimaryClick = s.primaryPressed && e.button === 0;
            isWheelClick = this._canWheelClick && s.wheelPressed && e.button === 1;
            isSecondaryClick = this._canSecondaryClick && s.secondaryPressed && e.button === 2;
            if (!isPrimaryClick && !isSecondaryClick && !isWheelClick && !e.isForced) return;
        }

        if (this._canControl(e)) e.preventDefault();
        else if (!s.pressed && s.fingerCount <= 0) return;

        const activeEl = document.activeElement;
        if (activeEl && BLACKLISTED_TAGS[activeEl.tagName]) activeEl.blur();

        s.useTouch = isTouch || isPrimaryTouch;
        if (s.useTouch) s.canHover = false;

        const activeTouches = isTouch ? e.touches : EMPTY_ARRAY;
        s.realFingerCount = activeTouches.length;

        let needsPosUpdate = false;
        if (activeTouches.length > 0) {
            for (let i = 0; i < this._fingers.length; i++) {
                const finger = this._fingers[i];
                if (!finger.used) continue;
                let remainsUsed = false;
                for (let j = 0; j < activeTouches.length; j++) {
                    if (finger.id === activeTouches[j].identifier) { remainsUsed = true; break; }
                }
                if (remainsUsed) continue;
                finger.unuse();
                s.fingerCount--;
                needsPosUpdate = true;
            }
        } else if (s.fingerCount > 0) {
            this._finger1.unuse();
            this._finger2.unuse();
            s.fingerCount = 0;
            needsPosUpdate = true;
        }

        if (!needsPosUpdate) return;

        if (s.fingerCount <= 0) {
            this.target.value = null;
            s.fingerCount = 0;
            s.pressed = false;
            s.primaryPressed = false;
            s.wheelPressed = false;
            s.secondaryPressed = false;
            s.element = null;
            s.pressure = 0;

            const now = performance.now();
            const elapsed = now - this._pressedAt;
            const d = dist(0, 0, s.relativePos.x, s.relativePos.y);
            const timeLimit = s.useTouch ? 500 : 250;
            if (d < 10 && elapsed < timeLimit && !s.isMultitouch) this._needsClicked = true;

            this._needsClickOut = true;
            this._needsClickIn = false;
            s.isMultitouch = false;

            const tapLimit = s.useTouch ? 350 : 250;
            if (d < 10 && elapsed < tapLimit) {
                if (this._tapFingerCount !== s.maxFingerCount) this._tapDate = 0;
                if (now - this._tapDate < tapLimit) this._needsDoubleTap = true;
                this._tapDate = now;
                this._tapFingerCount = s.maxFingerCount;
            } else {
                this._tapDate = this._tapFingerCount = 0;
            }

            s.maxFingerCount = 0;
        }

        this._updatePositions(true);
        this._updateNormalizePos();
    }
}
